/**
 * UsersTab.tsx — the admin Users tab (staff directory + invite drawer).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the form-on-top layout made the tab feel like a single-purpose invite
 * screen. The redesign flips it: a top bar (heading + "Create user") opens
 * the stepped invite wizard (InviteUser — it owns its own Drawer now, since
 * the wizard's Back/Continue live in the Drawer footer; mounted only while
 * open so each open starts fresh), and the tab body is a staff directory
 * table sourced from GET /v1/identity/admin/users — active accounts AND
 * invites still pending acceptance, so an admin can see the whole picture in
 * one place. The Name column populates for ADMINs too now that identity
 * stores fullName on every invite (not just clinicians), and a pending
 * invite row shows the invitee's name. After a successful invite the table
 * is refetched (the new pending row appears) while the drawer keeps showing
 * the one-time link so it can still be copied.
 *
 * ACTIONS (v0.6.3/v0.6.4/users-menu/G2): a Type column shows the prescriber
 * type (clinician rows only); a trailing per-row ⋯ menu (the shared accessible
 * {@link Menu}) gives a pending invite "Resend invite" (shows the fresh
 * single-use link inline) + "Revoke" (simple ConfirmDialog), and an active
 * user "Send password reset link" (mints + copies a one-time link, identity
 * also emails it), "Manage clinician record" (clinician rows only — the
 * audited edit Drawer, G2) + "Delete" (danger — the shared ConfirmDialog with
 * an OPTIONAL audit reason replaced the old inline confirm, G2). The Delete
 * item is HIDDEN entirely — never just
 * disabled — on the signed-in admin's own row and on itops@thegpservice.co.uk:
 * a courtesy mirroring the gateway's own refusal, which remains the real
 * control. Two "Last login"/"Last logout" columns (identity now returns
 * lastLoginAt/lastLogoutAt) show WHEN a staff account was last active, rendered
 * in true-UTC via formatUtcDateTime (— when null). Every action refetches on
 * success.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { createStaffResetLink, deleteStaffUser, getStaffUsers, resendInvite, revokeInvite } from '../api/identity';
import type { PrescriberType, StaffPendingInvite, StaffResetLink, StaffUserRow } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { formatUtcDate, formatUtcDateTime } from '../lib/format';
import { buildInviteLink, CopyLinkButton, SectionTitle } from '../staff/ui';
import { DataTable, Td, Th } from '../ui/Table';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { Menu, MenuItem } from '../ui/Menu';
import { Badge, Button, ErrorState, Help } from '../ui/kit';
import { TableSkeleton } from '../ui/skeletons';
import { toneFor } from '../ui/tones';
import { ClinicianRecordDrawer } from './ClinicianRecordDrawer';
import { InviteUser } from './InviteUser';

/** The gateway refuses to delete this account regardless of what the client sends — hide the action here too. */
const ITOPS_EMAIL = 'itops@thegpservice.co.uk';

const PRESCRIBER_TYPE_LABEL: Record<PrescriberType, string> = {
  doctor: 'Doctor',
  independent_prescriber: 'Independent prescriber',
  pharmacist: 'Pharmacist',
};

export function UsersTab() {
  const [users, setUsers] = useState<StaffUserRow[] | null>(null);
  const [pendingInvites, setPendingInvites] = useState<StaffPendingInvite[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const load = useCallback(() => {
    // A background refetch (after a row action — resend/revoke/delete) must
    // NOT blank the table back to a spinner: that would unmount every row's
    // local state (e.g. a just-shown "resend" link) before it can be seen.
    // Only the true first load / retry-from-error shows the spinner.
    setStatus((prev) => (prev === 'ready' ? prev : 'loading'));
    getStaffUsers()
      .then((data) => {
        setUsers(data.users);
        setPendingInvites(data.pendingInvites);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const empty = status === 'ready' && (users?.length ?? 0) === 0 && (pendingInvites?.length ?? 0) === 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--gps-s-3)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--gps-s-3)', flexWrap: 'wrap' }}>
        <SectionTitle>Clinicians &amp; admins</SectionTitle>
        <Button type="button" variant="cta" style={{ width: 'auto', minHeight: '44px', padding: '10px 18px' }} onClick={() => setDrawerOpen(true)}>
          Create user
        </Button>
      </div>

      {status === 'loading' ? (
        // Wave 6 §1: row-shaped loading — the staff table's columns as rows.
        <TableSkeleton rows={5} columns={6} label="Loading the staff list…" />
      ) : status === 'error' || !users || !pendingInvites ? (
        <ErrorState message="Sorry — we could not load the staff list." onRetry={load} />
      ) : empty ? (
        <Help style={{ marginBottom: 0 }}>No clinicians yet — create the first invite.</Help>
      ) : (
        <StaffUsersTable users={users} pendingInvites={pendingInvites} onChanged={load} />
      )}

      {/* Mounted only while open — every open starts a fresh wizard at step 1. */}
      {drawerOpen ? <InviteUser onClose={() => setDrawerOpen(false)} onInvited={load} /> : null}
    </div>
  );
}

function StaffUsersTable({
  users,
  pendingInvites,
  onChanged,
}: {
  users: StaffUserRow[];
  pendingInvites: StaffPendingInvite[];
  onChanged: () => void;
}) {
  const { me } = useAuth();
  return (
    <DataTable
      label="Clinicians and admins"
      head={
        <>
          <Th>Name</Th>
          <Th>Email</Th>
          <Th>Role</Th>
          <Th>Type</Th>
          <Th>Registration no.</Th>
          <Th>Status</Th>
          <Th>Joined</Th>
          <Th>Last login</Th>
          <Th>Last logout</Th>
          <Th>
            <span className="gps-sr-only">Actions</span>
          </Th>
        </>
      }
    >
      {users.map((user) => {
        const hideDelete = user.userId === me?.userId || user.email === ITOPS_EMAIL;
        return (
          <tr key={user.userId}>
            <Td>{user.fullName || '—'}</Td>
            <Td>{user.email}</Td>
            <Td>
              <Badge tone="neutral">{user.role}</Badge>
            </Td>
            <Td>{user.prescriberType ? PRESCRIBER_TYPE_LABEL[user.prescriberType] : '—'}</Td>
            <Td>{user.registrationNumber || '—'}</Td>
            <Td>
              <Badge tone={toneFor('active')}>Active</Badge>
            </Td>
            <Td>{formatUtcDate(user.createdAt)}</Td>
            <Td style={{ whiteSpace: 'nowrap' }}>{user.lastLoginAt ? formatUtcDateTime(user.lastLoginAt) : '—'}</Td>
            <Td style={{ whiteSpace: 'nowrap' }}>{user.lastLogoutAt ? formatUtcDateTime(user.lastLogoutAt) : '—'}</Td>
            <Td>
              <ActiveUserActions user={user} hideDelete={hideDelete} onChanged={onChanged} />
            </Td>
          </tr>
        );
      })}
      {pendingInvites.map((invite) => (
        <tr key={`pending-${invite.email}`}>
          <Td>{invite.fullName || '—'}</Td>
          <Td>{invite.email}</Td>
          <Td>
            <Badge tone="neutral">{invite.role}</Badge>
          </Td>
          <Td>—</Td>
          <Td>—</Td>
          <Td>
            <Badge tone="pending">Invite pending · expires {formatUtcDate(invite.expiresAt)}</Badge>
          </Td>
          <Td>—</Td>
          <Td>—</Td>
          <Td>—</Td>
          <Td>
            <PendingInviteActions email={invite.email} onChanged={onChanged} />
          </Td>
        </tr>
      ))}
    </DataTable>
  );
}

/** The shared block that shows an action's inline feedback (error/link) beneath the row's ⋯ menu. */
const actionFeedbackStyle: CSSProperties = { display: 'grid', gap: '4px', marginTop: '6px', minWidth: '11rem' };

/** A friendly error line under a row action (role=alert so it's announced). */
function ActionError({ children }: { children: string }) {
  return (
    <span role="alert" style={{ color: 'var(--gps-danger-text)', fontSize: '12px' }}>
      {children}
    </span>
  );
}

/** A minted one-time link shown as a copyable code block (shared by resend + reset). */
function InlineLink({ value, children }: { value: string; children?: ReactNode }) {
  return (
    <div style={{ fontSize: '12px', display: 'grid', gap: '2px' }}>
      {children}
      <code style={{ display: 'block', wordBreak: 'break-all', background: 'var(--gps-surface-2)', padding: '4px 6px', borderRadius: 'var(--gps-r-sm)' }}>
        {value}
      </code>
      <CopyLinkButton value={value} variant="link" />
    </div>
  );
}

/**
 * Active-user row actions behind the shared ⋯ menu: "Send password reset link"
 * (mints a one-time link — identity ALSO emails it — shown as a copy
 * fallback), "Manage clinician record" (clinician rows only — the audited
 * admin edit Drawer, G2) and, unless hidden for self/itops, "Delete" (danger)
 * which now gates behind the shared ConfirmDialog with an OPTIONAL reason
 * that lands in the identity audit log (replacing the old inline confirm —
 * never a silent one-click delete). The menu closes on select; result UI
 * renders in the row beneath the kebab so it survives the menu closing.
 */
function ActiveUserActions({ user, hideDelete, onChanged }: { user: StaffUserRow; hideDelete: boolean; onChanged: () => void }) {
  const { userId, email } = user;
  const [confirming, setConfirming] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetLink, setResetLink] = useState<StaffResetLink | null>(null);
  const [managingRecord, setManagingRecord] = useState(false);

  async function handleReset() {
    setResetBusy(true);
    setResetError(null);
    setResetLink(null);
    try {
      setResetLink(await createStaffResetLink(userId));
    } catch {
      setResetError('Sorry — we could not create a reset link.');
    } finally {
      setResetBusy(false);
    }
  }

  async function handleDelete(reason?: string) {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteStaffUser(userId, reason);
      setConfirming(false);
      onChanged();
    } catch {
      setDeleteError('Sorry — we could not delete this user.');
      setConfirming(false);
      setDeleteBusy(false);
    }
  }

  return (
    <>
      <Menu menuLabel={`Actions for ${email}`} triggerLabel={`Actions for ${email}`}>
        {({ close }) => (
          <>
            <MenuItem
              disabled={resetBusy}
              onSelect={() => {
                close();
                void handleReset();
              }}
            >
              {resetBusy ? 'Creating…' : 'Send password reset link'}
            </MenuItem>
            {user.role === 'CLINICIAN' ? (
              <MenuItem
                onSelect={() => {
                  close();
                  setManagingRecord(true);
                }}
              >
                Manage clinician record
              </MenuItem>
            ) : null}
            {hideDelete ? null : (
              <MenuItem
                tone="danger"
                onSelect={() => {
                  close();
                  setConfirming(true);
                }}
              >
                Delete
              </MenuItem>
            )}
          </>
        )}
      </Menu>

      {resetError || resetLink || deleteError ? (
        <div style={actionFeedbackStyle}>
          {resetError ? <ActionError>{resetError}</ActionError> : null}
          {resetLink ? (
            <InlineLink value={resetLink.resetUrl}>
              <span style={{ color: 'var(--gps-text)' }}>Reset link created — the email has also been sent</span>
              <span style={{ color: 'var(--gps-muted)' }}>Expires {formatUtcDateTime(resetLink.expiresAt)}</span>
            </InlineLink>
          ) : null}
          {deleteError ? <ActionError>{deleteError}</ActionError> : null}
        </div>
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          title={`Delete ${email}?`}
          tone="danger"
          body="Their account is removed and they can no longer sign in."
          optionalReason
          reasonLabel="Reason (recorded in the audit log)"
          confirmLabel="Delete user"
          busy={deleteBusy}
          onConfirm={(reason) => void handleDelete(reason)}
          onCancel={() => setConfirming(false)}
        />
      ) : null}

      {/* Mounted only while open so each open starts from the row's current record. */}
      {managingRecord ? <ClinicianRecordDrawer user={user} onClose={() => setManagingRecord(false)} onSaved={onChanged} /> : null}
    </>
  );
}

/** Pending-invite row actions behind the shared ⋯ menu: resend (shows the fresh single-use link) + revoke (gated behind a simple ConfirmDialog, G2). */
function PendingInviteActions({ email, onChanged }: { email: string; onChanged: () => void }) {
  const [busy, setBusy] = useState<'resend' | 'revoke' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [freshLink, setFreshLink] = useState<string | null>(null);
  const [confirmingRevoke, setConfirmingRevoke] = useState(false);

  async function handleResend() {
    setBusy('resend');
    setError(null);
    try {
      const result = await resendInvite(email);
      setFreshLink(buildInviteLink(result.token));
      onChanged();
    } catch {
      setError('Sorry — we could not resend the invite.');
    } finally {
      setBusy(null);
    }
  }

  async function handleRevoke() {
    setBusy('revoke');
    setError(null);
    try {
      await revokeInvite(email);
      setConfirmingRevoke(false);
      onChanged();
    } catch {
      setError('Sorry — we could not revoke the invite.');
      setConfirmingRevoke(false);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <Menu menuLabel={`Actions for ${email}`} triggerLabel={`Actions for ${email}`}>
        {({ close }) => (
          <>
            <MenuItem
              disabled={busy !== null}
              onSelect={() => {
                close();
                void handleResend();
              }}
            >
              {busy === 'resend' ? 'Resending…' : 'Resend invite'}
            </MenuItem>
            <MenuItem
              disabled={busy !== null}
              onSelect={() => {
                close();
                setConfirmingRevoke(true);
              }}
            >
              {busy === 'revoke' ? 'Revoking…' : 'Revoke'}
            </MenuItem>
          </>
        )}
      </Menu>

      {error || freshLink ? (
        <div style={actionFeedbackStyle}>
          {error ? <ActionError>{error}</ActionError> : null}
          {freshLink ? <InlineLink value={freshLink} /> : null}
        </div>
      ) : null}

      {confirmingRevoke ? (
        <ConfirmDialog
          open
          title={`Revoke the invite for ${email}?`}
          body="Their invite link stops working immediately."
          confirmLabel="Revoke invite"
          busy={busy === 'revoke'}
          onConfirm={() => void handleRevoke()}
          onCancel={() => setConfirmingRevoke(false)}
        />
      ) : null}
    </>
  );
}
